// ============================================================
// CuttingTab.jsx  —  Noble Enterprises (tape module)
//
// PHASE 2 OF PRODUCTION: cores → finished tape
//
// WHAT A RUN IS
// The operator picks a WIP group, says how many cores he cut,
// gives the finished size, and how many pieces came off. The
// cores leave the pool whole and their cost lands on the
// pieces.
//
// THE POOL IS GROUPED BY FULL SPEC
// Type, micron, core width, AND core length. A 70-yard core
// and a 150-yard core of the same width are not
// interchangeable — they yield different numbers of pieces, so
// they are different groups and a run cuts one group at a time.
//
// THE CALCULATOR
//   across = floor(core width  ÷ finished width)
//   along  = floor(core length ÷ finished length)
//   per core = across × along
// Both divisors are real. A 1260mm × 70yd core cut to 48mm ×
// 35yd gives 26 across and 2 along — 52 pieces. The leftover
// strip on each axis is trim and is not tracked.
//
// PIECES IS CAPPED, NOT COMPUTED
// The operator types what actually came off, because bad cuts
// happen. The cap only stops a number that no core could have
// produced — that would put stock on the shelf nothing paid
// for.
//
// COST IS VISIBLE HERE ONLY IN THE FORM PREVIEW. Production
// screens show quantities; the money lives in Finance.
//
// HOW THIS FILE IS ORGANISED
//   A. Shared UI bits
//   B. WIP pool
//   C. RunForm
//   D. CuttingTab
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, X, Check, ChevronDown as Chevron } from 'lucide-react';
import WindowModal from '../components/WindowModal';
import { hasFullAccess } from '../../../utils/access';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  warning:    '#d97706',
  warningBg:  '#fef3c7',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
};

const num    = (n) => Number(n || 0);
const money  = (n) => `Rs ${num(n).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
const today  = () => new Date().toISOString().split('T')[0];
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB');

// Trailing zeros off the numerics Postgres sends as strings
const mm  = (n) => `${num(n).toLocaleString('en-PK', { maximumFractionDigits: 2 })} mm`;
const yd  = (n) => `${num(n).toLocaleString('en-PK', { maximumFractionDigits: 2 })} yd`;

// The whole yield model in one function. Floor on each axis
// independently — the remainder of each is trim, not a smaller
// piece.
const perCorePieces = (coreW, coreLen, finW, finLen) => {
  const across = Math.floor(num(coreW) / num(finW));
  const along  = Math.floor(num(coreLen) / num(finLen));
  if (!(across > 0) || !(along > 0)) return { across: 0, along: 0, perCore: 0 };
  return { across, along, perCore: across * along };
};


// ============================================================
// A. SHARED UI BITS
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, children, hint }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
    </label>
    {children}
    {hint && <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '5px' }}>{hint}</div>}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, title, children, ...props }) => (
  <button
    title={title}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: danger ? C.danger : C.textSoft, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

const thStyle = {
  textAlign: 'left', padding: '11px 18px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 18px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};

const StatCard = ({ label, value, colour = C.text, sub }) => (
  <div style={{
    background: C.card, border: `1px solid ${C.border}`,
    borderRadius: '10px', padding: '16px 18px',
  }}>
    <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
      {label}
    </div>
    <div style={{ fontSize: '21px', fontWeight: '700', color: colour, marginTop: '4px' }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>{sub}</div>}
  </div>
);

const specLabel = (g) =>
  `${g.tape_type_name} ${num(g.micron_value)}µ · ${mm(g.core_width_mm)} × ${yd(g.core_length_yards)}`;


// ============================================================
// B. POOL SELECTION
//
// The pool is no longer a wall of cards. Three dropdowns walk
// down the grouping the WIP endpoint already returns — type →
// micron → core length — and each level lists only what the
// level above actually holds, with its core count on the
// option. Core width is not a level: cores are always 1260 mm.
// ============================================================

const Select = ({ value, onChange, disabled, placeholder, options }) => (
  <select
    value={value}
    onChange={onChange}
    disabled={disabled}
    style={{
      ...inputStyle,
      cursor: disabled ? 'default' : 'pointer',
      background: disabled ? C.rowAlt : '#ffffff',
      color: disabled ? C.textMuted : C.text,
    }}
  >
    <option value="">{placeholder}</option>
    {options.map(o => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

const coresIn = (rows) => rows.reduce((s, r) => s + Number(r.cores_available || 0), 0);

// Distinct values at one level, each carrying the cores sitting
// underneath it. sortKey is the numeric column to order by;
// without it the label sorts alphabetically.
const levelOptions = (rows, key, labelFor, sortKey) => {
  const map = new Map();
  rows.forEach(r => {
    const k = String(r[key]);
    if (!map.has(k)) map.set(k, { value: k, row: r, cores: 0 });
    map.get(k).cores += Number(r.cores_available || 0);
  });

  const out = [...map.values()];
  out.sort((a, b) => sortKey
    ? num(a.row[sortKey]) - num(b.row[sortKey])
    : String(labelFor(a.row)).localeCompare(String(labelFor(b.row))));

  return out.map(o => ({
    value: o.value,
    label: `${labelFor(o.row)} (${o.cores} core${o.cores === 1 ? '' : 's'})`,
  }));
};


// ============================================================
// C. RUN FORM
//
// In a window rather than inline, so the pool stays visible
// behind it and a half-finished run can be minimised while the
// operator checks something else.
//
// The calculator is the point. Both divisors are shown, so an
// operator who typed 35 instead of 70 sees the piece count
// double before he saves.
// ============================================================

const RunForm = ({ groups, picked, setPicked, form, setForm, onSave, onCancel, saving }) => {
  const [sel, setSel] = useState({ type: '', micron: '', length: '' });

  const byType   = groups.filter(g => String(g.tape_type_id) === sel.type);
  const byMicron = byType.filter(g => String(g.micron_id) === sel.micron);

  const typeOpts   = levelOptions(groups,   'tape_type_id',      g => g.tape_type_name);
  const micronOpts = levelOptions(byType,   'micron_id',         g => `${num(g.micron_value)}µ`, 'micron_value');
  const lenOpts    = levelOptions(byMicron, 'core_length_yards', g => yd(g.core_length_yards),   'core_length_yards');

  // One WIP row per spec in practice, since width is fixed —
  // but sum anyway so the count can never lie.
  const resolve = (type, micron, length) => {
    const rows = groups.filter(g =>
      String(g.tape_type_id) === type &&
      String(g.micron_id) === micron &&
      String(g.core_length_yards) === length
    );
    if (rows.length === 0) return null;
    return {
      ...rows[0],
      cores_available: coresIn(rows),
      total_cost: rows.reduce((s, r) => s + num(r.total_cost), 0),
    };
  };

  // Changing a level clears everything below it.
  const pickType   = (v) => { setSel({ type: v, micron: '', length: '' }); setPicked(null); };
  const pickMicron = (v) => { setSel(s => ({ ...s, micron: v, length: '' })); setPicked(null); };
  const pickLength = (v) => {
    setSel(s => ({ ...s, length: v }));
    setPicked(v ? resolve(sel.type, sel.micron, v) : null);
  };

  const ready   = !!picked;
  const coreW   = ready ? num(picked.core_width_mm) : 0;
  const coreLen = ready ? num(picked.core_length_yards) : 0;
  const avail   = ready ? Number(picked.cores_available) : 0;

  // Average, because the pool is fungible — the exact cores are
  // picked FIFO on the server.
  const costPerCore = avail > 0 ? num(picked.total_cost) / avail : 0;

  const cores  = Number(form.cores_used) || 0;
  const finW   = num(form.width_mm);
  const finLen = num(form.length_yards);
  const pieces = Number(form.pieces_produced) || 0;

    const sizeGiven = ready && finW > 0 && finLen > 0;
  const { across, along, perCore } = sizeGiven
    ? perCorePieces(coreW, coreLen, finW, finLen)
    : { across: 0, along: 0, perCore: 0 };

  const maxPieces = perCore * cores;

  const tooManyCores = cores > avail;
  const cutImpossible = sizeGiven && perCore === 0;
  const tooManyPieces = pieces > maxPieces && maxPieces > 0;

    const role = JSON.parse(localStorage.getItem('user') || '{}').role;
  const canSeeCost = hasFullAccess('adhesive');

    const inputCost   = costPerCore * cores;

  // --- production expenses ---
  const expenses = form.expenses || [];
  const setExpense = (i, patch) =>
    setForm({ ...form, expenses: expenses.map((e, j) => (j === i ? { ...e, ...patch } : e)) });
  const addExpense = () =>
    setForm({ ...form, expenses: [...expenses, { name: '', amount: '', fixed: false }] });
  const removeExpense = (i) =>
    setForm({ ...form, expenses: expenses.filter((_, j) => j !== i) });

  const expenseTotal = expenses.reduce(
    (s, e) => s + (Number(e.amount) > 0 ? Number(e.amount) : 0), 0
  );
  // A line with an amount needs a name; nothing may be negative.
  const expensesOk = expenses.every(e =>
    (e.amount === '' || Number(e.amount) >= 0) &&
    (!(Number(e.amount) > 0) || String(e.name || '').trim())
  );

  const costPerPiece = pieces > 0 ? (inputCost + expenseTotal) / pieces : 0;

  // Pieces short of what the cores could have given. Not an
  // error — bad cuts are real — but worth seeing.
  const shortfall = maxPieces > 0 && pieces > 0 ? maxPieces - pieces : 0;

    const canSave =
    ready && !saving && cores > 0 && !tooManyCores &&
    sizeGiven && !cutImpossible &&
    pieces > 0 && !tooManyPieces &&
        !!form.run_date && expensesOk;

    return (
    <WindowModal
      id="cut-run-form"
      title="Record cut run"
           subtitle={
        ready
          ? `${specLabel(picked)} — ${avail} core${avail === 1 ? '' : 's'} in pool`
          : 'Pick a type, micron and core length'
      }
      onClose={onCancel}
      width="820px"
    >
      {/* The headline number. Nothing below it means anything
          until the spec is settled, so it leads. */}
      <div style={{
        textAlign: 'center', paddingBottom: '16px', marginBottom: '18px',
        borderBottom: `1px solid ${C.borderSoft}`,
      }}>
        <div style={{
          fontSize: '11px', fontWeight: '600', color: C.textMuted,
          textTransform: 'uppercase', letterSpacing: '0.3px',
        }}>
          Cores available
        </div>
        <div style={{ fontSize: '36px', fontWeight: '700', color: C.brand, lineHeight: 1.15, marginTop: '2px' }}>
          {ready ? avail : '—'}
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
        gap: '16px', marginBottom: '18px',
      }}>
        <Field label="Tape type">
          <Select
            value={sel.type}
            onChange={e => pickType(e.target.value)}
            placeholder="Select type"
            options={typeOpts}
          />
        </Field>

        <Field label="Micron">
          <Select
            value={sel.micron}
            onChange={e => pickMicron(e.target.value)}
            disabled={!sel.type}
            placeholder="Select micron"
            options={micronOpts}
          />
        </Field>

        <Field label="Core length" hint="Cores are always 1260 mm wide">
          <Select
            value={sel.length}
            onChange={e => pickLength(e.target.value)}
            disabled={!sel.micron}
            placeholder="Select length"
            options={lenOpts}
          />
        </Field>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
        gap: '16px', marginBottom: '18px',
        opacity: ready ? 1 : 0.55,
      }}>
        <Field label="Cores used" hint="Whole cores only">
          <TextInput
            type="number" min="1" step="1" max={avail}
            disabled={!ready}
            value={form.cores_used}
            onChange={e => setForm({ ...form, cores_used: e.target.value })}
            placeholder="e.g. 4"
          />
        </Field>

        <Field label="Finished width (mm)" hint={`Core is ${ready ? mm(coreW) : '1260 mm'}`}>
          <TextInput
            type="number" step="0.01" min="0"
            disabled={!ready}
            value={form.width_mm}
            onChange={e => setForm({ ...form, width_mm: e.target.value })}
            placeholder="e.g. 48"
          />
        </Field>

        <Field label="Finished length (yards)" hint={`Core is ${ready ? yd(coreLen) : '—'}`}>
          <TextInput
            type="number" step="0.01" min="0"
            disabled={!ready}
            value={form.length_yards}
            onChange={e => setForm({ ...form, length_yards: e.target.value })}
            placeholder="e.g. 35"
          />
        </Field>

        <Field
          label="Pieces produced"
          hint={maxPieces > 0 ? `${maxPieces} is the most this cut gives` : undefined}
        >
          <TextInput
            type="number" min="1" step="1"
            disabled={!ready}
            value={form.pieces_produced}
            onChange={e => setForm({ ...form, pieces_produced: e.target.value })}
            placeholder={maxPieces > 0 ? String(maxPieces) : '0'}
          />
        </Field>

        <Field label="Run date">
          <TextInput
            type="date" max={today()}
            disabled={!ready}
            value={form.run_date}
            onChange={e => setForm({ ...form, run_date: e.target.value })}
          />
        </Field>

        <Field label="Notes">
          <TextInput
            disabled={!ready}
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            placeholder="Optional"
          />
        </Field>
      </div>

            {/* --- production expenses: all optional --- */}
      <div style={{
        background: '#ffffff', border: `1px solid ${C.border}`,
        borderRadius: '8px', padding: '16px 18px', marginBottom: '18px',
        opacity: ready ? 1 : 0.55,
      }}>
        <div style={{
          fontSize: '12px', fontWeight: '600', color: C.textSoft,
          textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '12px',
        }}>
          Production expenses
          <span style={{ color: C.textMuted, fontWeight: '500', textTransform: 'none' }}>
            {' '}— optional, added to this run's pieces
          </span>
        </div>

        {expenses.map((e, i) => (
          <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ flex: '1 1 200px' }}>
              {e.fixed ? (
                <div style={{ fontSize: '14px', fontWeight: '600', color: C.text }}>{e.name}</div>
              ) : (
                <TextInput
                  disabled={!ready}
                  value={e.name}
                  onChange={ev => setExpense(i, { name: ev.target.value })}
                  placeholder="Expense name"
                />
              )}
            </div>
            <div style={{ flex: '0 0 160px' }}>
              <TextInput
                type="number" step="0.01" min="0"
                disabled={!ready}
                value={e.amount}
                onChange={ev => setExpense(i, { amount: ev.target.value })}
                placeholder="Rs 0"
              />
            </div>
            <div style={{ flex: '0 0 32px' }}>
              {!e.fixed && (
                <IconBtn danger title="Remove expense" onClick={() => removeExpense(i)}>
                  <Trash2 size={15} />
                </IconBtn>
              )}
            </div>
          </div>
        ))}

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
          <Btn variant="ghost" onClick={addExpense} disabled={!ready}>
            <Plus size={15} /> Add expense
          </Btn>
          {expenseTotal > 0 && (
            <span style={{ fontSize: '13px', fontWeight: '600', color: C.textSoft }}>
              Expenses total {money(expenseTotal)}
            </span>
          )}
          {!expensesOk && (
            <span style={{ fontSize: '12px', color: C.danger }}>
              Give every added expense a name, and no negative amounts.
            </span>
          )}
        </div>
      </div>

      {tooManyCores && (
        <div style={{
          background: C.dangerBg, color: C.danger, borderRadius: '8px',
          padding: '13px 16px', marginBottom: '18px', fontSize: '13px',
        }}>
          Only {avail} core{avail === 1 ? '' : 's'} in this group.
        </div>
      )}

      {cutImpossible && (
        <div style={{
          background: C.dangerBg, color: C.danger, borderRadius: '8px',
          padding: '13px 16px', marginBottom: '18px', fontSize: '13px', lineHeight: 1.5,
        }}>
          A {mm(coreW)} × {yd(coreLen)} core cannot give a {mm(finW)} × {yd(finLen)} piece.
          The finished size has to fit inside the core on both axes.
        </div>
      )}

      {/* Live calculator */}
      {cores > 0 && sizeGiven && !cutImpossible && (
        <div style={{
          background: '#ffffff',
          border: `1px solid ${tooManyPieces ? C.danger : C.border}`,
          borderRadius: '8px', padding: '16px 18px', marginBottom: '18px',
        }}>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '16px',
          }}>
            {[
              ['Across', `${across} ×`,        C.textSoft],
              ['Along',  `${along} ×`,         C.textSoft],
              ['Per core', `${perCore}`,       C.text],
              ['Max pieces', `${maxPieces}`,   C.success],
              ['Entered', pieces > 0 ? `${pieces}` : '—', tooManyPieces ? C.danger : C.text],
            ].map(([label, value, colour]) => (
              <div key={label}>
                <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
                  {label}
                </div>
                <div style={{ fontSize: '17px', fontWeight: '700', color: colour, marginTop: '3px' }}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          <div style={{
            marginTop: '14px', paddingTop: '12px',
            borderTop: `1px solid ${C.borderSoft}`,
            display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
            fontSize: '12px', color: C.textMuted,
          }}>
            <span>
              {mm(coreW)} ÷ {mm(finW)} = {across} across
              {' · '}
              {yd(coreLen)} ÷ {yd(finLen)} = {along} along
            </span>
                        {canSeeCost && pieces > 0 && !tooManyPieces && (
              <span style={{ marginLeft: 'auto', fontWeight: '600', color: C.textSoft }}>
                {money(inputCost + expenseTotal)} in · {money(costPerPiece)} per piece
              </span>
            )}
          </div>

          {shortfall > 0 && !tooManyPieces && (
            <div style={{
              marginTop: '12px', padding: '10px 13px', borderRadius: '7px',
              background: C.warningBg, color: C.warning, fontSize: '13px',
            }}>
              {shortfall} piece{shortfall === 1 ? '' : 's'} short of what these cores could give.
              The cost of the shortfall lands on the pieces that were made.
            </div>
          )}

          {tooManyPieces && (
            <div style={{
              marginTop: '12px', padding: '10px 13px', borderRadius: '7px',
              background: C.dangerBg, color: C.danger, fontSize: '13px',
            }}>
              {cores} core{cores === 1 ? '' : 's'} cannot give {pieces} pieces at this size.
              {maxPieces} is the most.
            </div>
          )}
        </div>
      )}

            <div style={{ display: 'flex', gap: '10px' }}>
        <Btn onClick={onSave} disabled={!canSave}>
          <Check size={16} /> {saving ? 'Saving…' : 'Record run'}
        </Btn>
        <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
      </div>
    </WindowModal>
  );
};


// ============================================================
// D. MAIN COMPONENT
// ============================================================

const emptyForm = () => ({
  cores_used: '',
  width_mm: '',
  length_yards: '',
  pieces_produced: '',
    run_date: today(),
  notes: '',
  // Production expenses: all optional. Fixed lines can't be
  // renamed or removed; added lines can.
  expenses: [
    { name: 'Labour',  amount: '', fixed: true },
    { name: 'Packing', amount: '', fixed: true },
  ],
});

const CuttingTab = () => {
  const [groups, setGroups]   = useState([]);
  const [runs, setRuns]       = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked]   = useState(null);
  const [form, setForm]       = useState(null);
  const [saving, setSaving]   = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [detail, setDetail]   = useState({});
  const [banner, setBanner]   = useState({ type: '', message: '' });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  const load = useCallback(async () => {
    try {
      const [w, r, s] = await Promise.all([
        axios.get(`${API}/tape/cut-runs/wip`, { headers }),
        axios.get(`${API}/tape/cut-runs`, { headers }),
        axios.get(`${API}/tape/cut-runs/summary`, { headers }),
      ]);
      setGroups(w.data.data || []);
      setRuns(r.data.data || []);
      setSummary(s.data.data || null);
    } catch {
      notify('error', 'Could not load cutting. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

    const openForm = () => {
    setPicked(null);
    setForm(emptyForm());
  };

  const cancel = () => {
    setPicked(null);
    setForm(null);
  };

  // Only fetched when a row is opened — most never are.
  const toggleRow = async (row) => {
    if (expandedId === row.id) { setExpandedId(null); return; }
    setExpandedId(row.id);

    if (!detail[row.id]) {
      try {
        const res = await axios.get(`${API}/tape/cut-runs/${row.id}`, { headers });
        setDetail(d => ({ ...d, [row.id]: res.data.data }));
      } catch {
        notify('error', 'Could not load that run');
      }
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/tape/cut-runs`, {
        tape_type_id:      picked.tape_type_id,
        micron_id:         picked.micron_id,
        core_width_mm:     picked.core_width_mm,
        core_length_yards: picked.core_length_yards,
        cores_used:        Number(form.cores_used),
        width_mm:          Number(form.width_mm),
        length_yards:      Number(form.length_yards),
        pieces_produced:   Number(form.pieces_produced),
        run_date:          form.run_date,
                notes:             form.notes,
        expenses:          (form.expenses || []).map(e => ({ name: e.name, amount: e.amount })),
      }, { headers });

      notify('success', 'Cut run recorded');
      cancel();
      setDetail({});
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(
      `Delete run ${row.run_number}? The cores go back to the pool and the pieces leave finished stock.`
    )) return;

    try {
      await axios.delete(`${API}/tape/cut-runs/${row.id}`, { headers });
      notify('success', 'Run deleted');
      if (expandedId === row.id) setExpandedId(null);
      setDetail({});
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete');
    }
  };

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '14px', marginBottom: '20px',
      }}>
        <StatCard label="Cores in pool"  value={summary?.cores_in_pool ?? '—'} colour={C.brand} />
        <StatCard label="Cut this month" value={summary?.pieces_this_month ?? '—'} sub="pieces" />
        <StatCard label="Total runs"     value={summary?.run_count ?? '—'} />
        <StatCard
          label="Pieces made"
          value={summary?.pieces_produced ?? '—'}
          sub={summary ? `of ${summary.pieces_possible} possible` : undefined}
        />
      </div>
      {/* --- runs --- */}
      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              Cut runs
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              Click a run to see which core batches it consumed.
            </p>
          </div>

                   {!form && (
            !loading && groups.length === 0 ? (
              <div style={{
                background: C.infoBg, color: C.info, borderRadius: '8px',
                padding: '9px 14px', fontSize: '13px',
              }}>
                No cores in the pool. Record core production first.
              </div>
            ) : (
              <Btn onClick={openForm} disabled={loading}>
                <Plus size={16} /> Record run
              </Btn>
            )
          )}
        </div>

               <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Run</th>
                <th style={thStyle}>Cut to</th>
                <th style={thStyle}>From cores</th>
                <th style={thStyle}>Date</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Cores</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Pieces</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Left</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && runs.map(row => {
                const isOpen    = expandedId === row.id;
                const produced  = Number(row.pieces_produced);
                const remaining = Number(row.qty_remaining);
                const short     = Number(row.pieces_short);
                const d         = detail[row.id];

                return (
                  <React.Fragment key={row.id}>
                    <tr
                      onClick={() => toggleRow(row)}
                      style={{ cursor: 'pointer', background: isOpen ? C.rowAlt : 'transparent' }}
                    >
                      <td style={{ ...tdStyle, fontWeight: '700' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                          <Chevron
                            size={15}
                            style={{
                              color: C.textMuted,
                              transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                              transition: 'transform 0.15s',
                            }}
                          />
                          {row.run_number}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        {mm(row.width_mm)} × {yd(row.length_yards)}
                        <div style={{ fontSize: '11px', color: C.textMuted }}>
                          {row.tape_type_name} {num(row.micron_value)}µ
                        </div>
                      </td>
                      <td style={{ ...tdStyle, color: C.textSoft }}>
                        {mm(row.core_width_mm)} × {yd(row.core_length_yards)}
                      </td>
                      <td style={{ ...tdStyle, color: C.textSoft }}>{fmtDate(row.run_date)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{row.cores_used}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>
                        {produced}
                        {short > 0 && (
                          <div style={{ fontSize: '11px', color: C.warning }}>
                            {short} short
                          </div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <span style={{
                          fontWeight: '600',
                          color: remaining === 0 ? C.textMuted : C.brand,
                        }}>
                          {remaining}
                        </span>
                        {remaining < produced && (
                          <div style={{ fontSize: '11px', color: C.textMuted }}>
                            {produced - remaining} sold
                          </div>
                        )}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {remaining === produced && (
                          <IconBtn
                            danger
                            title="Delete run"
                            onClick={(e) => { e.stopPropagation(); remove(row); }}
                          >
                            <Trash2 size={15} />
                          </IconBtn>
                        )}
                      </td>
                    </tr>

                    {isOpen && (
                      <tr>
                        <td colSpan="8" style={{
                          padding: '18px 22px', background: C.rowAlt,
                          borderTop: `1px solid ${C.borderSoft}`,
                        }}>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                            gap: '12px', marginBottom: '14px',
                          }}>
                            {[
                              ['Cut to',      `${mm(row.width_mm)} × ${yd(row.length_yards)}`],
                              ['From core',   `${mm(row.core_width_mm)} × ${yd(row.core_length_yards)}`],
                              ['Cores used',  row.cores_used],
                              ['Pieces made', produced],
                              ['Most possible', row.max_pieces],
                              ['Still in stock', remaining],
                            ].map(([label, value]) => (
                              <div key={label} style={{
                                background: '#ffffff', border: `1px solid ${C.border}`,
                                borderRadius: '8px', padding: '12px 14px',
                              }}>
                                <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
                                  {label}
                                </div>
                                <div style={{ fontSize: '14px', fontWeight: '700', color: C.text, marginTop: '3px' }}>
                                  {value}
                                </div>
                              </div>
                            ))}
                          </div>

                          <div style={{
                            fontSize: '11px', color: C.textMuted, fontWeight: '600',
                            textTransform: 'uppercase', marginBottom: '8px',
                          }}>
                            Cores came from
                          </div>

                          {d?.batches?.length ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                              {d.batches.map((b, i) => (
                                <span key={i} style={{
                                  fontSize: '13px', fontWeight: '600', color: C.text,
                                  background: '#ffffff', border: `1px solid ${C.border}`,
                                  borderRadius: '8px', padding: '5px 11px',
                                }}>
                                  {b.batch_number || 'unknown batch'}
                                  <span style={{ color: C.textMuted, fontWeight: '500' }}>
                                    {' '}× {b.cores}
                                  </span>
                                  {b.vendor_roll_number && (
                                    <span style={{ color: C.textMuted, fontWeight: '500' }}>
                                      {' '}· {b.vendor_roll_number}
                                    </span>
                                  )}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <div style={{ fontSize: '13px', color: C.textMuted }}>Loading…</div>
                          )}

                                                    {d?.expenses?.length > 0 && (
                            <>
                              <div style={{
                                fontSize: '11px', color: C.textMuted, fontWeight: '600',
                                textTransform: 'uppercase', margin: '14px 0 8px',
                              }}>
                                Production expenses
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {d.expenses.map((e, i) => (
                                  <span key={i} style={{
                                    fontSize: '13px', fontWeight: '600', color: C.text,
                                    background: '#ffffff', border: `1px solid ${C.border}`,
                                    borderRadius: '8px', padding: '5px 11px',
                                  }}>
                                    {e.name}
                                    <span style={{ color: C.textMuted, fontWeight: '500' }}>
                                      {' '}{money(e.amount)}
                                    </span>
                                  </span>
                                ))}
                              </div>
                            </>
                          )}

                          {row.notes && (
                            <div style={{ marginTop: '12px', fontSize: '13px', color: C.textSoft }}>
                              <strong style={{ color: C.text }}>Notes:</strong> {row.notes}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              {!loading && runs.length === 0 && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                                        No cut runs yet. Hit “Record run” to start one.
                  </td>
                </tr>
              )}
            </tbody>
                    </table>
        </div>
      </div>

           {form && (
        <RunForm
          groups={groups}
          picked={picked}
          setPicked={setPicked}
          form={form}
          setForm={setForm}
          onSave={save}
          onCancel={cancel}
          saving={saving}
        />
      )}
    </div>
  );
};

export default CuttingTab;